import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, Loader2, Search, SquareCheckBig } from "lucide-react";
import { searchBrief, type BriefCard, type BriefProject } from "@slumbr/core";

import { useBriefStore } from "../store";

export function SearchPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const activeWorkspaceId = useBriefStore((s) => s.activeWorkspaceId);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<{ projects: BriefProject[]; cards: BriefCard[] }>({ projects: [], cards: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!activeWorkspaceId || q.trim().length < 2) {
      setResults({ projects: [], cards: [] });
      return;
    }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      void searchBrief(activeWorkspaceId, q.trim())
        .then((r) => { if (alive) setResults(r); })
        .catch(() => undefined)
        .finally(() => { if (alive) setLoading(false); });
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q, activeWorkspaceId]);

  const go = (path: string) => { navigate(path); onClose(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 pt-[12vh]" onClick={onClose}>
      <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-black/10 px-4 py-3 dark:border-white/10">
          <Search className="h-5 w-5 opacity-50" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
            placeholder="Search projects and cards…"
            className="flex-1 bg-transparent text-sm outline-none"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin opacity-50" />}
        </div>

        <div className="max-h-[50vh] overflow-y-auto p-2">
          {q.trim().length < 2 ? (
            <p className="px-3 py-6 text-center text-sm opacity-40">Type at least 2 characters</p>
          ) : results.projects.length === 0 && results.cards.length === 0 && !loading ? (
            <p className="px-3 py-6 text-center text-sm opacity-40">No results</p>
          ) : (
            <>
              {results.projects.length > 0 && (
                <div className="mb-1">
                  <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide opacity-40">Projects</p>
                  {results.projects.map((p) => (
                    <button key={p.id} onClick={() => go(`/brief/projects/${p.id}`)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5">
                      <FolderKanban className="h-4 w-4 opacity-60" />
                      <span className="flex-1 truncate">{p.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {results.cards.length > 0 && (
                <div>
                  <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wide opacity-40">Cards</p>
                  {results.cards.map((c) => (
                    <button key={c.id} onClick={() => go(`/brief/boards/${c.boardId}`)} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5">
                      <SquareCheckBig className="h-4 w-4 opacity-60" />
                      <span className="flex-1 truncate">{c.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
